import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePrincipal } from '@/core/auth/appToken';
import { isOrganisationGesperrt } from '@/core/auth/organisationGesperrt';

interface RequireAccountEnabledProps {
  readonly children: ReactNode;
}

/** Sperrseite for a deactivated organization (mirrors `LoginForm`'s redirect target). */
const ACCOUNT_DISABLED_PATH = '/account-deaktiviert';

/**
 * Route guard keeping a principal whose active organization is `DISABLED` on
 * `/account-deaktiviert`, regardless of how they got there — deep-link, full-page reload, or
 * history navigation, not only the post-login redirect handled by `LoginForm`. Mirrors
 * {@link RequireOnboardingComplete} exactly: wraps the business route tree under `/`,
 * inside `RequireAuth` and ahead of `RequireOnboardingComplete`.
 *
 * A principal that has not (yet) resolved is passed through unchanged — `RequireAuth`, which
 * always wraps this guard, is responsible for the unauthenticated case. `/account-deaktiviert`
 * itself is registered as a top-level route outside this guard's subtree, so it is never
 * re-entered here; the current-location exemption below is a defensive guard in case that route
 * is ever nested back under `/`, to rule out a redirect loop.
 */
export function RequireAccountEnabled({ children }: RequireAccountEnabledProps) {
  const principal = usePrincipal();
  const location = useLocation();

  if (isOrganisationGesperrt(principal) && location.pathname !== ACCOUNT_DISABLED_PATH) {
    return <Navigate to={ACCOUNT_DISABLED_PATH} replace />;
  }

  return <>{children}</>;
}
